'use client';
import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';

import { CartBadge } from '@eliadmin/core/src/components/header/cart-badge';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCartQueryOpts } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';
import { lazy, Suspense, useEffect, useRef } from 'react';

const HeaderCartPopover = lazy(() => import('./header-cart-popover'));
import { usePathname } from 'next/navigation';
import { FaCartShopping } from 'react-icons/fa6';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type HeaderCartButtonProps = {
  className?: {
    root?: string;
    info?: string;
    title?: string;
    price?: string;
    badge?: string;
  };
};

export function HeaderCartButton({ className }: HeaderCartButtonProps) {
  const systemLabels = useSystemLabels();
  const templateSettings = useGlobalStore(state => state.templateSettings);
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const { isOpen, setIsOpen } = useDisclosure();
  const pathname = usePathname();
  const { t } = useTranslations();

  useEffect(() => {
    setIsOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const timerRef = useRef<number | undefined>(undefined);

  const handleMouseOver = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timerRef.current = window.setTimeout(() => {
      setIsOpen(false);
    }, 100) as unknown as number; // Type casting to number
  };

  const { data: cart, isLoading } = useQuery(useCartQueryOpts());

  const totalPriceNoVat = cart?.items_total_without_vat ?? 0;

  return (
    <div className="relative">
      <Link
        href={ROUTES.CART() ?? '#'}
        prefetch={false}
        className="relative"
        onMouseOver={handleMouseOver}
        onMouseLeave={handleMouseLeave}
      >
        <span className="sr-only">{systemLabels('cart')} </span>
        <Button
          asChild
          variant="outline"
          className={cn(
            'w-fit text-center',
            'relative flex h-10 w-10 items-center justify-center gap-2 border border-border px-4 shadow-none hover:bg-neutral-50 sm:h-[44px] sm:w-auto',
            'ea-cart-button',
            className?.root,
          )}
        >
          <div data-ea-visual-tag="button">
            <div className="relative flex h-5 w-5 items-center gap-1">
              {
                <FaCartShopping
                  size={20}
                  className="h-[18px] w-[18px] text-foreground sm:h-5 sm:w-5"
                />
              }

              <CartBadge
                className={cn(
                  'h-5 w-5 bg-destructive-foreground font-light hover:bg-destructive-foreground',
                  className?.badge,
                )}
                variant={HEADER_CONFIG.headerMain.cart.badgeVariant}
              />
            </div>

            {(!!templateSettings.header?.cart_text?.enabled ||
              !!templateSettings.realty_header?.cart_text?.enabled) && (
              <div className={cn('hidden sm:grid', 'ea-cart-button__info', className?.info)}>
                {/* <p className={cn('mb-1 leading-4', 'ea-cart-button__title', className?.title)}>
              {t('text163')}
            </p> */}
                <p
                  className={cn(
                    'leading-2 min-w-[40px] pr-2 text-end text-sm font-semibold text-muted-foreground',
                    'ea-cart-button__price',
                    className?.price,
                  )}
                >
                  {formatPrice(
                    templateSettings?.product_detail?.price_without_vat_first?.enabled
                      ? totalPriceNoVat
                      : cart?.items_total || 0,
                    currency,
                    currencySettings,
                  )}
                </p>
              </div>
            )}
          </div>
        </Button>
      </Link>
      {isOpen && (
        <div
          onMouseOver={handleMouseOver}
          onMouseLeave={handleMouseLeave}
          className="absolute left-[-308px] top-12 z-50 min-h-40 w-[440px] rounded-sm border border-gray-100 bg-background p-2 shadow-lg"
        >
          <Suspense
            fallback={
              <div role="status" className="flex h-40 items-center justify-center">
                {t('text456')}
              </div>
            }
          >
            <HeaderCartPopover
              cart={cart}
              currency={currency}
              currencySettings={currencySettings}
            />
          </Suspense>
        </div>
      )}
    </div>
  );
}
